import React, { useState, useEffect, useRef } from "react";
import {
    AlertCircle, Users, UserPlus, Filter, Calendar, Check,
    CalendarDays, Coins, ChevronDown, ChevronRight, X, Send, MousePointerClick, Target
} from 'lucide-react';
import {getPoolSpots} from "../../api/spot/spot.api";
import {getCountExpectedFriend} from "../../api/moment/moment.api";
import {getExpectedCustomerCount, getMomentAvailable, getSentOnetimeMessages} from "../../api/message/volatility-message.api";
import {getShippingRegionTree} from "../../api/region/region.api";
import {getPoolBenefits, getPoolProjects} from "../../api/pool/pool-option.api";

// --- [Data Definition] ---
/** 발송일 표시용. `2026-07-15` 형태. 값이 없으면 빈 문자열. */
const sendDate = (iso) => (iso ? String(iso).slice(0, 10) : '');
/** 호버 상세용. `2026-07-15 14:30` 형태. */
const sendDateTime = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '알 수 없음');

/** 미리보기 조회 debounce. 체크박스 연속 클릭이 그대로 호출로 나가지 않게 한다. */
const PREVIEW_DEBOUNCE_MS = 400;

/**
 * 자체 툴팁.
 *
 * 🔴 네이티브 `title` 도, omoplay `QuestionTooltip` 도 이 화면에서는 동작하지 않았다.
 * 원인 = 이 아이콘들이 `<label>` 안에 있어서다. `<label>` 은 자식의 포인터 이벤트를
 * 연결된 컨트롤(라디오)로 **재타겟**하므로 자식의 hover 가 안정적으로 잡히지 않는다.
 * → hover 를 직접 state 로 관리하고, 아이콘을 `<label>` **밖**에 두어 해결한다.
 */
const InfoTip = ({ text }) => {
    const [pos, setPos] = useState(null);   // null = 닫힘
    const ref = useRef(null);

    // 🔴 absolute 로 띄우면 안 된다 — 1·2단계 카드가 `overflow-hidden` 이라 잘린다.
    //    (실제로 툴팁이 열리지만 내용이 보이지 않는 증상이 났다)
    //    position: fixed + 버튼의 화면 좌표로 띄워 모든 overflow 컨텍스트를 벗어난다.
    const open = () => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        const width = 288;                                   // w-72
        const left = Math.min(Math.max(8, r.right - width), window.innerWidth - width - 8);
        setPos({ top: r.bottom + 6, left });
    };

    return (
        <span className="inline-flex flex-shrink-0"
              onMouseEnter={open}
              onMouseLeave={() => setPos(null)}>
            <button ref={ref} type="button" tabIndex={-1}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); pos ? setPos(null) : open(); }}
                    className="w-4 h-4 rounded-full border border-amber-400 text-amber-500 text-[10px] leading-none font-bold flex items-center justify-center hover:bg-amber-50">
                ?
            </button>
            {pos && (
                <span style={{ position: 'fixed', top: pos.top, left: pos.left, width: 288 }}
                      className="z-[7000] bg-gray-800 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2 shadow-xl text-left whitespace-pre-line block">
                    {text}
                </span>
            )}
        </span>
    );
};

// ⚠️ 종전의 createClickHistoryOptions(= click_history 트리 노드)는 제거했다.
//    클릭 조건은 '반응 조건'이므로 Layer 2 로 옮겼고, 거기서 라디오로 직접 렌더한다.
//    기존 respond_1/2/3 와이어 포맷과 의미는 그대로 유지한다(저장된 예약 무영향).


// 2. 배송지 주소 데이터
// 🔴 종전에는 시·도 주소 트리가 이 파일에 하드코딩되어 있었고 **8개 시·도만** 있었다
//    (대전·광주·울산·세종·충북·충남·경북·경남·전남 9개 누락).
//    이제 백엔드 `GET /region/shipping` 에서 받는다 — 배송지 필터가 실제로 조회하는
//    `zip_code` 테이블에서 만든 트리라 어휘 불일치가 구조적으로 불가능하다.
//    (날씨/진입점 매칭용 `GET /region/tree` 는 forecast_info 기반이라 소스가 다르다)

// --- [Component Start] ---
const OneTimeMessageSegment = ({
                                   isNotSendMoment, momentBlockReason = null,
                                   totalCount, cost, memberCount, nonMemberCount, targets, setTargets,
                                   initialData, onUpdate, setMemberCount, setNonMemberCount, onTestSend
                               }) => {
    /**
     * 저장된 구 필터를 Layer 1 형태로 옮긴다.
     *
     * 프로젝트·혜택을 3단계에서 1단계로 올렸으므로, 그 전에 저장된 예약은
     * `project: [id]` / `benefit: [id]` 형태다. 그대로 두면 **화면에 아무것도 안 보인다**
     * (3단계에서 제거했고 1단계는 pool_type 을 보므로) → 조건이 사라진 것처럼 보이는 사고가 된다.
     *
     * 이미 `pool_type` 이 있으면 손대지 않는다. 둘 다 있으면 프로젝트를 우선한다
     * (Layer 1 은 한 종류만 고를 수 있으므로) — 나머지는 그대로 남겨 백엔드가 AND 로 적용한다.
     */
    const normalizeLegacyFilters = (f) => {
        if (!f || typeof f !== 'object') return {};
        if (f.pool_type) return f;
        const legacy = f.project ? 'PROJECT' : (f.benefit ? 'BENEFIT' : null);
        if (!legacy) return f;
        const next = { ...f };
        const key = legacy === 'PROJECT' ? 'project' : 'benefit';
        next.pool_type = [legacy];
        next.pool_ids = next[key].map(String);
        delete next[key];
        return next;
    };

    const [selectedFilters, setSelectedFilters] = useState(normalizeLegacyFilters(initialData?.selectedFilters));
    const [isAdult, setIsAdult] = useState(initialData?.isAdult || false);
    const [reserveDate, setReserveDate] = useState({ date: '', hour: '08', min: '00' });

    const [expandedCategories, setExpandedCategories] = useState([]);
    const [sentMessages, setSentMessages] = useState([]);
    const [addressTree, setAddressTree] = useState([]);
    const [poolSpots, setPoolSpots] = useState([]);
    const [spotPickerOpen, setSpotPickerOpen] = useState(false);
    const [spotSearch, setSpotSearch] = useState('');
    const [spotTypeFilter, setSpotTypeFilter] = useState('ALL');   // ALL | OFFLINE | ONLINE
    const [spotRegion, setSpotRegion] = useState('');              // '' = 전체 지역
    const [genericSearch, setGenericSearch] = useState('');        // 프로젝트·혜택 picker 검색어
    const [poolBenefits, setPoolBenefits] = useState([]);
    const [poolProjects, setPoolProjects] = useState([]);
    /** 비회원 친구(모먼트) 발송 가능 여부. null = 확인 중 (확인 전에는 막지 않는다). */
    const [momentAvailable, setMomentAvailable] = useState(null);
    const [showZeroSpots, setShowZeroSpots] = useState(false);
    // 퍼널 — 단계별 잔존 인원. 1단계는 모수만, 2단계는 모수+반응, 3단계(=최종)는 전부 적용.
    const [stageCounts, setStageCounts] = useState({ target: 0, engagement: 0 });
    const [messagePickerOpen, setMessagePickerOpen] = useState(false);
    const [messageSearch, setMessageSearch] = useState('');
    const [totalCost, setTotalCost] = useState(cost);

    // ── Layer 1 (Target) / Layer 2 (Engagement) 파생 상태 ──
    // 단일 진실원천은 selectedFilters 다. 별도 state 를 두면 두 곳이 어긋난다.
    const poolType = selectedFilters.pool_type?.[0] || 'ALL';
    const poolIds = selectedFilters.pool_ids || [];
    const reactType = selectedFilters.react_type?.[0] || 'ALL';
    const reactCount = selectedFilters.react_count?.[0] || '1';
    const isMessagePool = poolType === 'ONETIME_MSG';

    // ── 기간 필터 (pool_period) ──
    // 값 형식 = "YYYY-MM-DD~YYYY-MM-DD" (절대 구간).
    // 상대 기간("최근 1개월")을 저장하면 예약 시점과 발송 시점의 기준이 달라져
    // 상세 화면에서 언제를 뜻하는지 알 수 없어진다 → 절대 구간으로 저장한다(003 §5).
    const poolPeriod = (selectedFilters.pool_period?.[0] || '').split('~');
    const periodFrom = poolPeriod[0] || '';
    const periodTo = poolPeriod[1] || '';

    /** 기간의 의미는 pool 종류가 결정한다 (003 §1). */
    const PERIOD_LABEL = {
        ALL: '가입 기간',
        ONETIME_MSG: '발송 기간',
        ENTRY_POINT: '방문 기간',
        PROJECT: '참여 기간',
        BENEFIT: '발급 기간',
    };
    const PERIOD_HELP = {
        ALL: '채널에 친구추가한 시점 기준입니다.',
        ONETIME_MSG: '선택한 메시지가 발송된 시점 기준입니다. 고객이 클릭한 시점이 아닙니다.',
        ENTRY_POINT: '고객이 그 진입점으로 처음 유입된 시점 기준입니다.',
        PROJECT: '고객이 프로젝트에 참여한 시점 기준입니다.',
        BENEFIT: '혜택이 발급된 시점 기준입니다. 사용한 시점이 아닙니다.',
    };

    const changePeriod = (from, to) => {
        setSelectedFilters(prev => {
            const f = { ...prev };
            if (!from && !to) delete f.pool_period;
            else f.pool_period = [`${from}~${to}`];
            return f;
        });
    };

    /** Layer 1 변경 — pool 계열이 바뀌면 Layer 2 선택을 초기화한다(기준 메시지가 달라지므로). */
    const changePoolType = (next) => {
        setSelectedFilters(prev => {
            const f = { ...prev };
            // 🔴 pool 종류가 바뀌면 **대상 id 와 기간을 반드시 버린다.**
            //    종전에는 `if (!f.pool_ids) f.pool_ids = []` 라 기존 id 가 살아남았고,
            //    그 id 가 새 pool 의 목록(진입점·프로젝트…)에서 재해석되어
            //    **체크는 안 됐는데 chip 에는 엉뚱한 항목이 뜨는** 현상이 났다.
            //    기간도 의미(발송/방문/참여/발급)가 달라지므로 함께 버린다.
            delete f.pool_ids; delete f.pool_period;
            // 반응 조건의 기준도 사라진다.
            delete f.react_type; delete f.react_count; delete f.react_period;
            delete f.respond_1; delete f.respond_2; delete f.respond_3;
            if (next === 'ALL') delete f.pool_type;
            else f.pool_type = [next];
            return f;
        });
    };

    const togglePoolId = (id) => {
        setSelectedFilters(prev => {
            const cur = prev.pool_ids || [];
            const v = String(id);
            const next = cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v];
            // 🔴 종전에는 여기서 pool_type 을 ['ONETIME_MSG'] 로 **무조건 덮어썼다.**
            //    그래서 '진입점 방문 고객' 에서 진입점을 체크하면 모수가 메시지로 튀었다.
            //    pool_type 은 changePoolType 만 바꾼다 — 여기서는 건드리지 않는다.
            const f = { ...prev, pool_ids: next };
            // 대상이 바뀌면 반응 조건의 기준도 바뀐다 → 대상이 비면 반응 조건을 지운다
            if (next.length === 0) { delete f.react_type; delete f.react_count; delete f.react_period; }
            return f;
        });
    };

    /** Layer 2 변경 */
    const changeReactType = (next) => {
        setSelectedFilters(prev => {
            const f = { ...prev };
            delete f.respond_1; delete f.respond_2; delete f.respond_3;
            if (next === 'ALL') { delete f.react_type; delete f.react_count; delete f.react_period; }
            else {
                f.react_type = [next];
                if (next === 'CLICKED') f.react_count = [prev.react_count?.[0] || '1'];
                else delete f.react_count;
                // 🔴 반응 기간을 쓰는 유형만 남긴다. `NOT_CLICKED` 는 백엔드가 **일부러 무시**하므로
                //    (사용자 결정 D2) 남겨두면 화면엔 기간 chip 이 보이는데 결과엔 안 반영돼 오해가 된다.
                //    혜택 발급/사용도 기간을 쓰므로(XS-08 G) 그때는 값을 **유지**한다 —
                //    '최근 3개월' 을 고른 사용자 입장에선 유형만 바꿨는데 기간이 사라지면 놀란다.
                if (!['CLICKED', 'BENEFIT_ISSUED', 'BENEFIT_USED'].includes(next)) delete f.react_period;
            }
            return f;
        });
    };

    const changeReactCount = (n) => setSelectedFilters(prev => ({ ...prev, react_type: ['CLICKED'], react_count: [String(n)] }));

    /**
     * Layer 2 반응(클릭) 기간 = `react_period` (XS-08 신설).
     *
     * 🔴 1단계 `pool_period` 와 **다른 축**이다 — 저쪽은 '메시지가 발송된 시점', 이쪽은 '고객이 누른 시점'.
     * 값 형식은 `respond_N` 과 **동일한 2종**(개월 프리셋 `"3"` / 절대 구간 `"from~to"`)이라
     * 백엔드가 같은 규칙으로 해석한다.
     * 🔴 '클릭함' 에서만 쓴다. 백엔드가 NOT_CLICKED 에는 적용하지 않으므로(D2) 상태도 함께 지운다.
     */
    const reactPeriodValue = selectedFilters.react_period?.[0] || '';
    const isReactPeriodCustom = String(reactPeriodValue).includes('~');
    const rpRange = isReactPeriodCustom ? String(reactPeriodValue).split('~') : ['', ''];
    /** select 표시값: 미설정은 빈 문자열(= 전체 기간), 절대 구간은 CUSTOM. */
    const reactPeriodSelect = reactPeriodValue === '' ? '' : (isReactPeriodCustom ? 'CUSTOM' : reactPeriodValue);
    const setReactPeriod = (raw) => setSelectedFilters(prev => {
        // 🔴 종전에는 여기서 react_type 을 ['CLICKED'] 로 **덮어썼다.** 클릭 전용이던 시절의 코드다.
        //    혜택 발급/사용도 같은 키를 쓰게 되면서(XS-08 G) 덮어쓰면 '혜택 사용' 을 고른 뒤
        //    기간을 바꾸는 순간 '클릭함' 으로 튄다. **현재 유형을 유지한다.**
        const f = { ...prev };
        const cur = prev.react_type?.[0];
        if (cur === 'CLICKED' && !f.react_count) f.react_count = [prev.react_count?.[0] || '1'];
        if (!raw) delete f.react_period; else f.react_period = [String(raw)];
        return f;
    });

    /**
     * 혜택 반응 한 줄을 그린다 — **모수 종류와 무관하게 같은 모양**이다(E: UI 통일).
     * 종전에는 이 두 옵션이 메시지 모수 분기 안에만 있었다(F: 다른 모수에도 필요).
     *
     * 🔑 기간 라벨이 유형에 따라 다르다 — 발급은 `issued_at`, 사용은 `used_at` 기준이라
     * 화면에도 그렇게 적어야 오해가 없다.
     */
    const renderBenefitRow = (value) => {
        const isUsed = value === 'BENEFIT_USED';
        const label = isUsed
            ? `${benefitScopeLabel} 받은 혜택을 사용한 고객`
            : `${benefitScopeLabel} 혜택 쿠폰을 발급받은 고객`;
        const desc = isUsed ? '혜택 사용 처리 기준' : '혜택 발급 기준 (발송 성공 여부는 따지지 않음)';
        const timeWord = isUsed ? '사용' : '발급';
        const warn = isUsed
            ? "비밀번호 없는 혜택은 고객이 직접 '사용하기'를 눌러야 기록됩니다. 매장에서의 실제 사용량과는 다를 수 있습니다."
            : '이 조건은 발송 성공을 따지지 않습니다. 혜택은 발송 성공을 기다리지 않고 발급되므로, 메시지가 도달하지 않은 고객도 포함됩니다(실측 100명).';
        const selected = reactType === value;
        return (
            <div key={value} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${selected ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'} ${poolIds.length === 0 ? 'opacity-40 pointer-events-none' : ''}`}>
                <input type="radio" name="reactType" id={`react-${value}`} className="mt-0.5 accent-emerald-600 cursor-pointer"
                       checked={selected} onChange={() => changeReactType(value)} />
                <span className="flex-1">
                    <label htmlFor={`react-${value}`} className="block cursor-pointer">
                        <span className="block text-sm text-gray-800">{label}</span>
                        <span className="block text-[11px] text-gray-500 mt-0.5">{desc}</span>
                    </label>
                    {selected && (
                        <span className="flex items-center gap-1.5 mt-2 flex-wrap">
                            <select value={reactPeriodSelect}
                                    onChange={(e) => {
                                        if (e.target.value === 'CUSTOM') {
                                            const to = new Date();
                                            const from = new Date(); from.setMonth(from.getMonth() - 1);
                                            const fmt = (d) => d.toISOString().slice(0, 10);
                                            setReactPeriod(`${fmt(from)}~${fmt(to)}`);
                                        } else {
                                            setReactPeriod(e.target.value);
                                        }
                                    }}
                                    className="text-xs border border-gray-300 rounded px-2 py-1 outline-none flex-shrink-0">
                                <option value="">{timeWord} 시점 전체</option>
                                {Array.from({length: 12}, (_, i) => (
                                    <option key={i+1} value={String(i+1)}>오늘부터 최근 {i+1}개월 이내 {timeWord}</option>
                                ))}
                                <option value="CUSTOM">{timeWord} 시점 직접 입력</option>
                            </select>
                            <InfoTip text={`혜택을 ${timeWord}한 시점 기준입니다.\n\n'최근 N개월' = 오늘로부터 거꾸로 N개월입니다.\n\n1단계의 기간(모수 시점)과는 다른 축입니다.`} />

                            {isReactPeriodCustom && (
                                <span className="flex items-center gap-1.5 flex-wrap basis-full mt-1">
                                    <span className="text-[11px] text-gray-600 font-semibold">{timeWord} 기간</span>
                                    <input type="date" value={rpRange[0] || ''}
                                           onChange={(e) => setReactPeriod(`${e.target.value}~${rpRange[1] || ''}`)}
                                           className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-emerald-400" />
                                    <span className="text-[11px] text-gray-400">~</span>
                                    <input type="date" value={rpRange[1] || ''}
                                           onChange={(e) => setReactPeriod(`${rpRange[0] || ''}~${e.target.value}`)}
                                           className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-emerald-400" />
                                    <button type="button" onClick={() => setReactPeriod('')}
                                            className="text-[11px] text-gray-400 hover:text-red-500 px-1">지우기</button>
                                </span>
                            )}

                            {/* 🔴 화면 안내를 넣지 않는다 (사용자 결정 2026-08-31).
                                `is_used=1 AND used_at IS NULL` 이 107건 있으나(전부 DELIVERY, 14~22개월 전)
                                **현재 코드로는 재발하지 않는다** — `is_used=true` 를 쓰는 유일 경로
                                `BenefitResultService.updateByUsed` 가 `used_at` 을 항상 같은 UPDATE 로 넣는다.
                                ⇒ 과거 데이터만의 이야기라 상시 경고로 띄우면 소음이 된다.
                                🔴 다만 그 107건은 기간을 지정하면 **조용히 제외**된다는 사실은 유효하다 —
                                   재발 여부는 `019 §2-2` 쿼리로 감시한다(늘어나면 새 경로가 생긴 것). */}
                        </span>
                    )}
                </span>
                <InfoTip text={warn} />
            </div>
        );
    };

    /**
     * 🔑 모수(1단계)별로 2단계에 노출할 **혜택 반응** 옵션 (2026-08-31, XS-08 F).
     *
     * 고객 피드백 = 진입점·프로젝트·혜택 모수에서도 '혜택 발급/사용' 을 걸고 싶다.
     * 백엔드는 **그 모수의 키로 혜택을 스코프**한다(진입점 → `spot_id`, 프로젝트 → `project_id`,
     * 혜택 → `benefit_coupon_id`, 메시지 → `message_id`). 전부 인덱스가 있다.
     *
     * 🔴 여기 없는 모수는 **의도적으로 비운 것**이다:
     * - `ALL`(전체 회원) — 스코프가 없어 `benefit_result` 약 91만 행 풀스캔이 된다
     * - `ENTRY_POINT_JOIN`(참여 이력) — `pool_ids` 가 진입점 id 라 `project_id` 로 스코프할 수 없다
     * 🔴 `BENEFIT` 모수에 '발급받은 고객' 이 없는 것도 의도다 — 이미 쿠폰으로 고른 모수와 **중복**이다
     *    (사용자 결정 2026-08-31).
     */
    const BENEFIT_REACTIONS = {
        ONETIME_MSG: ['BENEFIT_ISSUED', 'BENEFIT_USED'],
        ENTRY_POINT: ['BENEFIT_ISSUED', 'BENEFIT_USED'],
        PROJECT:     ['BENEFIT_ISSUED', 'BENEFIT_USED'],
        BENEFIT:     ['BENEFIT_USED'],
    };
    const benefitReactions = BENEFIT_REACTIONS[poolType] || [];

    /** 혜택 반응 옵션의 라벨 — 모수에 따라 '무엇의 혜택' 인지가 달라진다. */
    const benefitScopeLabel = {
        ONETIME_MSG: '위에서 선택한 메시지로',
        ENTRY_POINT: '위에서 선택한 진입점에서',
        PROJECT:     '위에서 선택한 프로젝트에서',
        BENEFIT:     '위에서 선택한 혜택을',
    }[poolType] || '';

    /** 비메시지 모수에서 쓰는 '채널 전체 클릭'(기존 respond_N). 의미·와이어 포맷 그대로. */
    const channelClickCategory = ['respond_1','respond_2','respond_3'].find(c => selectedFilters[c]) || null;
    const channelClickValue = channelClickCategory ? selectedFilters[channelClickCategory][0] : '2';
    /** 값에 '~' 가 있으면 직접 입력한 절대 구간이다. 없으면 개월 프리셋. */
    const isChannelClickCustom = String(channelClickValue).includes('~');
    const channelClickMonth = isChannelClickCustom ? 'CUSTOM' : channelClickValue;
    const ccRange = isChannelClickCustom ? String(channelClickValue).split('~') : ['', ''];
    const setChannelClick = (cat, month) => {
        setSelectedFilters(prev => {
            const f = { ...prev };
            delete f.respond_1; delete f.respond_2; delete f.respond_3;
            delete f.react_type; delete f.react_count; delete f.react_period;
            if (cat) f[cat] = [String(month)];
            return f;
        });
    };

    const selectedMessages = sentMessages.filter(m => poolIds.includes(String(m.id)));
    const selectedSpots = poolSpots.filter(sp => poolIds.includes(String(sp.id)));

    /** 진입점 주소는 `"시도 시군구"`. 온라인 진입점은 null 이다(설계상 정상). */
    const spotRegions = [...new Set(poolSpots.map(sp => sp.address).filter(Boolean))].sort();

    const hiddenZeroCount = poolSpots.filter(sp => sp.memberCount === 0).length;

    // 필터 트리 구조 정의 — Layer 3(고객 속성)
    // ⚠️ click_history 는 Layer 2 로 옮겼다(반응 조건이므로).
    const filterTree = [
        {
            category: 'gender',
            label: '성별',
            options: [
                { value: 'male', label: '남성' },
                { value: 'female', label: '여성' }
            ]
        },
        {
            category: 'age',
            label: '연령대',
            options: [
                { value: '15-19', label: '15-19세' },
                { value: '20-29', label: '20-29세' },
                { value: '30-39', label: '30-39세' },
                { value: '40-49', label: '40-49세' },
                { value: '50-59', label: '50-59세' },
                { value: '60-69', label: '60-69세' },
                { value: '70-79', label: '70-79세' },
                { value: '80-99', label: '80세 이상' }
            ]
        },
        {
            // Layer 1 의 '진입점 방문 고객'(최초 유입 기준, kakao_user.spot_id)과 다르다.
            // 이쪽은 project_join_history 의 참여 이력 기준이다 — 이름으로 구분해 준다.
            // options 를 주지 않는다 — renderSpotList 가 전용 UI 로 그린다(isSpotNode).
            category: 'entry_point',
            label: '진입점 참여 이력',
            note: '1단계의 \'진입점 방문 고객\'과 다릅니다.\n\n· 1단계 = 그 진입점으로 **처음 유입된** 고객 (1인 1곳)\n· 여기 = 그 진입점에서 **참여한 이력**이 있는 고객 (1인 여러 곳)\n\n여러 곳을 고르면 그 중 한 곳이라도 참여한 고객이 남습니다(OR).',
        },
        {
            category: 'addr',
            label: '배송지 주소',
            note: '배송지 정보가 있는 회원만 대상이 됩니다.',
            children: addressTree.map((city, index) => ({
                category: `address_${index}`,
                label: city.label,
                options: city.data.map(region => ({label: region.label, value: city.value + " " + region.value}))
            }))
        },
        // 프로젝트·혜택은 **Layer 1(발송 모수)로 올렸다**(2026-08-28).
        // 같은 소스인데 3단계에도 두면 순수 중복이고, 기간을 붙일 수 없다.
        // 백엔드의 `project`·`benefit` 카테고리는 **그대로 살아 있다** — 저장된 예약 호환용.
        // 구 필터는 아래 normalizeLegacyFilters 가 Layer 1 형태로 옮겨 준다.
    ];

    useEffect(() => {
        const v = initialData?.reserveDate;

        if (v === undefined) {
           setReserveDate({ date: '', hour: '08', min: '00' });
        } else if (typeof v === 'string') {
            const [date, time] = v.split(' ');
            const [hour, min] = time.split(':');

            setReserveDate({ date, hour, min });
        } else {
            setReserveDate(v);
        }

    }, []);

    useEffect(() => {
        // 진입점은 Layer 1·Layer 3 모두 renderSpotList(poolSpots) 를 쓴다.
        // 종전의 getAllSpots(id·name 만) 조회는 제거했다 — 타입·주소·회원수가 없어 쓸 수 없다.

        // 프로젝트·혜택은 Layer 1 로 올라가면서 picker 전용 API(getPoolProjects/getPoolBenefits)를 쓴다.
        // 종전의 listBenefit/getProjectList 조회는 제거했다 — Layer 3 에서 빠져 쓰이지 않는다.

        getSentOnetimeMessages().then(({data}) => {
            setSentMessages(Array.isArray(data) ? data : [])
        })

        getShippingRegionTree().then(({data}) => {
            setAddressTree(Array.isArray(data) ? data : [])
        })

        getPoolSpots().then(({data}) => {
            setPoolSpots(Array.isArray(data) ? data : [])
        })

        // 모수 picker 전용 목록 — 이름만으로는 고를 수 없어서 식별 정보를 더 받는다.
        getPoolBenefits().then(({data}) => setPoolBenefits(Array.isArray(data) ? data : []))
        getMomentAvailable().then(setMomentAvailable)
        getPoolProjects().then(({data}) => setPoolProjects(Array.isArray(data) ? data : []))
    }, []);

    useEffect(() => {
        setTotalCost(cost)
    }, [cost])

    useEffect(() => {
        if (!targets.member) {
            setMemberCount(0)
            setStageCounts({ target: 0, engagement: 0 })
            return
        }

        // 🔑 퍼널이므로 **단계별로 누적 적용한 값**이 필요하다.
        //    종전에는 전체 필터를 적용한 값 하나만 받아 1단계 칸에 그대로 찍었고,
        //    그래서 2단계 조건을 걸면 1단계 숫자까지 함께 줄어들어 퍼널로 읽히지 않았다.
        const subset = (pred) => Object.fromEntries(Object.entries(selectedFilters).filter(([k]) => pred(k)))
        const isPool = (k) => k.startsWith('pool_')
        const isReact = (k) => k.startsWith('react_') || k.startsWith('respond_')

        const stages = [
            subset(isPool),                          // 1단계: 모수만
            subset((k) => isPool(k) || isReact(k)),  // 2단계: 모수 + 반응
            selectedFilters,                         // 3단계: 전부 (= 최종)
        ]

        // 🔴 호출 비용이 작지 않다 — 그래서 두 가지 가드를 둔다.
        //
        // 미리보기 1회는 (1) `kakao_send_history` 에서 모수 행을 앱으로 끌어오고
        // (2) 그 id 로 `redirect_traced_result` 를 IN 조회하고 (3) 전화번호 IN 절로 최종 카운트한다.
        // 메시지 1건만 골라도 모수가 17,000행 규모가 될 수 있고, 그러면 IN 절 SQL 이 수백 KB 다.
        // 2026-07-21 Cloud SQL CPU 100% 인시던트가 바로 이 유형(UI 조작마다 무거운 쿼리)이었다.
        //
        // ① **중복 제거** — 단계별 필터셋이 같으면 같은 결과다. 2·3단계 조건이 없으면 3회가 아니라
        //    **1회**만 호출된다(2단계만 있으면 2회). 실사용 대부분이 여기 해당한다.
        // ② **debounce** — 종전에는 체크박스를 누를 때마다 즉시 호출했다.
        //    메시지 3건을 연속 선택하면 3회 × 3단계 = **9회**가 나갔다.
        const keys = stages.map((f) => JSON.stringify(Object.entries(f).sort()))
        const uniqueKeys = [...new Set(keys)]

        let cancelled = false
        const timer = setTimeout(() => {
            Promise.all(uniqueKeys.map((k) => {
                const f = stages[keys.indexOf(k)]
                return getExpectedCustomerCount(null, Object.keys(f).length > 0 ? "filter" : "total", f, false)
                    .then(({data}) => data)
                    .catch(() => 0)
            })).then((results) => {
                if (cancelled) return
                const byKey = new Map(uniqueKeys.map((k, i) => [k, results[i]]))
                const [target, engagement, total] = keys.map((k) => byKey.get(k))
                setStageCounts({ target, engagement })
                setMemberCount(total)
            })
        }, PREVIEW_DEBOUNCE_MS)

        return () => { cancelled = true; clearTimeout(timer) }
    }, [targets.member, selectedFilters]);

    useEffect(() => {
        if (targets.nonMember) {
            getCountExpectedFriend().then(({data}) => {
                if (data <= 10) {
                    alert("10명 이하는 전송할 수 없습니다.")
                    toggleTarget('nonMember')
                } else {
                    setNonMemberCount(data)
                }
            })
        } else if (!targets.nonMember) {
            setNonMemberCount(0)
        }
    }, [targets.nonMember]);

    useEffect(() => {
        // [New] 상태 변경 시 부모에게 업데이트 (데이터 유지용)
        if(onUpdate) {
            onUpdate({ selectedFilters, isAdult, reserveDate });
        }
    }, [selectedFilters, isAdult, reserveDate, onUpdate]);


    const toggleTarget = (key) => {
        // 🔴 비회원 친구(카카오 모먼트) 는 모든 채널이 보낼 수 있는 것이 아니다.
        //    카카오 광고계정을 제어하려면 우리 계정이 그 채널의 관리자여야 하고,
        //    그 결과로 `account_channel.kakao_moment_campaign_id` 가 생긴다.
        //    이 값이 없으면 **예약은 저장되고 발송만 실패**한다(스케줄러에서 `!!` 로 터진다)
        //    → 고객에게 가장 나쁜 형태이므로 **선택 자체를 막는다.**
        //    `momentAvailable === null` = 확인 중이므로 막지 않는다(조회 실패로 기능이 잠기는 것 방지).
        if (key === 'nonMember' && !targets.nonMember && momentAvailable === false) {
            alert('비회원 친구 발송은 추가 설정이 필요합니다. 관리자에게 문의하세요.');
            return;
        }
        const newTargets = { ...targets, [key]: !targets[key] };
        if (!newTargets.member && !newTargets.nonMember) return;
        setTargets(newTargets);
    };

    const toggleFilterOption = (category, value) => {
        setSelectedFilters(prev => {
            const current = prev[category] || [];
            const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
            const newFilters = { ...prev, [category]: updated };
            if (updated.length === 0) delete newFilters[category];
            return newFilters;
        });
    };

    const toggleExpand = (category) => {
        setExpandedCategories(prev =>
            prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]
        );
    };

    // 선택된 필터의 Label을 찾기 위한 헬퍼 함수
    const getFilterLabel = (category, value) => {
        const findInNodes = (nodes) => {
            for (const node of nodes) {
                // 1. 카테고리가 정확히 일치하고 옵션이 있는 경우 (2-depth 등)
                if (node.category === category && node.options) {
                    const found = node.options.find(opt => opt.value.toString() === value.toString());
                    if (found) return found.label;
                }

                // 2. 하위 자식 노드가 있는 경우 재귀 탐색 (3-depth 진입)
                if (node.children) {
                    const found = findInNodes(node.children);
                    // ★ 중요: 찾았을 때만 반환 (못 찾으면 null이므로 넘어감)
                    if (found) return found;
                }

                // 3. 동적 카테고리 처리 (배송지 주소 등: addr_서울특별시)
                // node.category가 존재하고, 찾는 category가 node.category로 시작하는 경우
                if (node.category && category.startsWith(node.category) && node.options) {
                    const found = node.options.find(opt => opt.value.toString() === value.toString());
                    if (found) return found.label;
                }
            }
            return null; // ★ 수정: 이 가지(branch)에서 못 찾으면 null 반환
        };

        // ── 트리에 없는 카테고리 (Layer 1·2 로 옮겼거나 신규) ──
        // click_history 노드를 Layer 2 로 옮기면서 respond_N 이 트리에서 사라졌다.
        // 그래서 라벨 탐색이 실패하고 원시값("2")이 chip 에 그대로 찍혔다.
        if (category === 'pool_type') {
            return {
                ONETIME_MSG: '모수: 지난 1회성 메시지 수신자',
                ENTRY_POINT: '모수: 진입점 방문 고객',
            }[value] || '모수: 전체 회원';
        }
        if (category === 'pool_ids') {
            // pool_type 에 따라 id 가 가리키는 대상이 다르다.
            // 라벨에 **모수 종류를 함께 넣어** pool_type chip 없이도 뜻이 통하게 한다.
            const find = (list, key) => list.find(x => String(x[key ?? 'id']) === String(value));
            switch (poolType) {
                case 'ENTRY_POINT': return `모수(진입점): ${find(poolSpots)?.name || `#${value}`}`;
                case 'PROJECT':     return `모수(프로젝트): ${poolProjects.find(x => String(x.id) === String(value))?.name || `#${value}`}`;
                case 'BENEFIT':     return `모수(혜택): ${poolBenefits.find(x => String(x.id) === String(value))?.name || `#${value}`}`;
                default:            return `모수(메시지): ${sentMessages.find(x => String(x.id) === String(value))?.campaignName || `#${value}`}`;
            }
        }
        if (category === 'react_type') {
            return {
                CLICKED: '반응: 클릭한 고객',
                NOT_CLICKED: '반응: 클릭하지 않은 고객',
                BENEFIT_ISSUED: '반응: 혜택 쿠폰 발급',
                BENEFIT_USED: '반응: 혜택 사용',
            }[value] || `반응: ${value}`;
        }
        if (category === 'pool_period') {
            const [f, t] = String(value).split('~');
            const unit = PERIOD_LABEL[poolType] || '기간';
            if (f && t) return `${unit}: ${f} ~ ${t}`;
            if (f) return `${unit}: ${f} 이후`;
            if (t) return `${unit}: ${t} 까지`;
            return unit;
        }
        if (category === 'react_count') return `${value}회 이상 클릭`;
        // 반응(클릭) 기간. 🔴 '발송 기간'(pool_period)과 구분되게 **'클릭'** 을 라벨에 박는다 —
        //    chip 두 개가 나란히 뜨면 어느 쪽이 어느 축인지 알 수 없다(XS-08).
        if (category === 'react_period') {
            if (String(value).includes('~')) {
                const [f, t] = String(value).split('~');
                const range = f && t ? `${f} ~ ${t}` : (f ? `${f} 이후` : `${t} 까지`);
                return `클릭 시점: ${range}`;
            }
            return `클릭 시점: 지난 ${value}개월`;
        }
        if (category.startsWith('respond_')) {
            const n = category.replace('respond_', '');
            if (String(value).includes('~')) {
                const [f, t] = String(value).split('~');
                const range = f && t ? `${f} ~ ${t}` : (f ? `${f} 이후` : `${t} 까지`);
                return `채널 전체 ${n}회 이상 클릭 (${range})`;
            }
            return `채널 전체 ${n}회 이상 클릭 (지난 ${value}개월)`;
        }

        // 트리 전체 탐색 후, 결과가 있으면 label 반환, 없으면 원래 value 반환
        const label = findInNodes(filterTree);
        return label || value;
    };

    /**
     * 선택된 필터 태그 리스트.
     *
     * 🔑 **단계 순서(1→2→3)로 정렬한다.** 종전에는 `Object.keys` 삽입 순서였고,
     * 사용자가 조건을 고치는 순서에 따라 chip 이 뒤섞여 읽기 어려웠다.
     */
    const CHIP_ORDER = [
        'pool_type', 'pool_ids', 'pool_period',                 // 1단계
        'react_type', 'react_count', 'react_period', 'respond_1', 'respond_2', 'respond_3',  // 2단계
        'gender', 'age',                                        // 3단계
    ];
    const chipRank = (cat) => {
        const i = CHIP_ORDER.indexOf(cat);
        if (i >= 0) return i;
        if (cat.startsWith('address')) return CHIP_ORDER.length;      // 3단계 주소
        return CHIP_ORDER.length + 1;                                  // 그 외
    };

    const renderSelectedTags = () => {
        const tags = [];
        Object.keys(selectedFilters).forEach(cat => {
            // 🔑 `pool_type` 단독 chip 은 **대상이 하나라도 선택됐으면 숨긴다.**
            //    대상 chip(`pool_ids`)이 모수 종류를 이미 품고 있어(예: "모수(메시지): …")
            //    두 chip 이 따로 있으면 종전처럼 한쪽만 지웠을 때 상태가 어긋나 보인다.
            if (cat === 'pool_type' && (selectedFilters.pool_ids?.length > 0)) return;
            selectedFilters[cat].forEach(val => {
                tags.push({ category: cat, value: val, label: getFilterLabel(cat, val) });
            });
        });
        return tags.sort((a, b) => chipRank(a.category) - chipRank(b.category));
    };

    /**
     * chip 의 X 를 눌렀을 때. 단순 토글이 아니라 **pool 계열의 결합을 지킨다.**
     *
     * - `pool_type` 제거 → 대상·기간·반응 조건까지 함께 정리(= 전체 회원으로 되돌림)
     * - `pool_ids` 마지막 항목 제거 → pool_type 은 남긴다(그 종류의 picker 를 계속 쓰게)
     */
    const removeChip = (category, value) => {
        if (category === 'pool_type') { changePoolType('ALL'); return; }
        if (category === 'pool_ids') { togglePoolId(value); return; }
        toggleFilterOption(category, value);
    };

    /**
     * 진입점 선택 목록. **Layer 1(모수) 과 Layer 3(참여 이력) 이 같은 UI 를 쓴다.**
     *
     * 두 곳의 의미는 다르다 —
     * Layer 1 = `kakao_user.spot_id`(최초 유입) / Layer 3 = `project_join_history`(참여 이력).
     * 그러나 **고르는 대상은 같은 진입점**이므로 목록·필터·검색을 공유한다.
     *
     * @param selectedValues 선택된 값 배열(문자열)
     * @param onToggle       항목 클릭 시 호출
     */
    const renderSpotList = (selectedValues, onToggle) => (
        <span className="block border border-gray-200 rounded-lg bg-white">
            {/* 타입 필터 — 온라인 진입점은 주소가 없어 지역으로 좁힐 수 없다 */}
            <span className="flex items-center gap-1 p-2 border-b border-gray-100 flex-wrap">
                {[['ALL', '전체'], ['OFFLINE', '오프라인'], ['ONLINE', '온라인']].map(([v, l]) => (
                    <button key={v} type="button"
                            onClick={() => { setSpotTypeFilter(v); if (v === 'ONLINE') setSpotRegion(''); }}
                            className={`text-[11px] px-2 py-1 rounded border ${spotTypeFilter === v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}>
                        {l}
                    </button>
                ))}
                <select value={spotRegion}
                        disabled={spotTypeFilter === 'ONLINE'}
                        onChange={(e) => setSpotRegion(e.target.value)}
                        className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none ml-1 disabled:bg-gray-100 disabled:text-gray-400">
                    <option value="">지역 전체</option>
                    {spotRegions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <InfoTip text={"온라인 진입점은 주소가 없어 지역으로 좁힐 수 없습니다(설계상 정상).\n\n오프라인 진입점의 주소는 점진적으로 입력되고 있어, 아직 주소가 없는 곳은 '지역 전체'에서만 보입니다."} />
            </span>

            <span className="block p-2 border-b border-gray-100">
                <input type="text" value={spotSearch}
                       onChange={(e) => setSpotSearch(e.target.value)}
                       placeholder="진입점 이름으로 검색"
                       className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded outline-none focus:border-indigo-400" />
            </span>

            <span className="block max-h-56 overflow-y-auto">
                {poolSpots.filter(sp => {
                    if (spotTypeFilter !== 'ALL' && sp.type !== spotTypeFilter) return false;
                    if (spotRegion && sp.address !== spotRegion) return false;
                    if (spotSearch && !sp.name.includes(spotSearch)) return false;
                    if (!showZeroSpots && sp.memberCount === 0 && !selectedValues.includes(String(sp.id))) return false;
                    return true;
                }).map(sp => (
                    <span key={sp.id}
                          onClick={() => onToggle(sp.id)}
                          className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b border-gray-50 last:border-0">
                        <input type="checkbox" readOnly className="accent-indigo-600"
                               checked={selectedValues.includes(String(sp.id))} />
                        <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded ${sp.type === 'OFFLINE' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                            {sp.type === 'OFFLINE' ? '오프라인' : sp.type === 'ONLINE' ? '온라인' : sp.type}
                        </span>
                        <span className="flex-1 truncate text-gray-800">{sp.name}</span>
                        <span className="text-gray-400 flex-shrink-0 truncate max-w-[120px]">{sp.address || ''}</span>
                        <span className="text-gray-500 flex-shrink-0 tabular-nums">회원 {(sp.memberCount ?? 0).toLocaleString()}</span>
                    </span>
                ))}
                {poolSpots.length === 0 && (
                    <span className="block px-3 py-4 text-xs text-gray-400 text-center">진입점이 없습니다.</span>
                )}
            </span>

            {hiddenZeroCount > 0 && (
                <span className="flex items-center justify-between p-2 border-t border-gray-100 text-[11px] text-gray-500">
                    <span>회원 유입이 없는 진입점 {hiddenZeroCount.toLocaleString()}곳{showZeroSpots ? '을 포함해 표시 중' : '은 숨겼습니다'}</span>
                    <button type="button" onClick={() => setShowZeroSpots(v => !v)}
                            className="px-2 py-0.5 rounded border border-gray-200 hover:bg-gray-50">
                        {showZeroSpots ? '숨기기' : '모두 보기'}
                    </button>
                </span>
            )}
        </span>
    );

    const renderTreeNodes = (nodes, depth = 0) => {
        return nodes.map((node) => {
            const isExpanded = expandedCategories.includes(node.category);
            const hasChildren = node.children && node.children.length > 0;
            const hasOptions = node.options && node.options.length > 0;
            /** 진입점 참여 이력은 옵션 칩 대신 전용 목록 UI 를 쓴다. */
            const isSpotNode = node.category === 'entry_point';
            /** 이 노드(및 하위)에서 선택된 값 개수 — 강조·배지에 쓴다. */
            const pickedCount = (() => {
                const own = (selectedFilters[node.category] || []).length;
                if (!hasChildren) return own;
                const childSum = node.children.reduce((n, c) => n + (selectedFilters[c.category] || []).length, 0);
                return own + childSum;
            })();

            return (
                <div key={node.category} className="mb-2">
                    <div
                        className={`flex items-center py-2 px-3 rounded-lg cursor-pointer select-none border
                            ${depth === 0 ? 'font-bold' : 'text-sm ml-4 pl-3'}
                            ${pickedCount > 0
                                ? 'bg-indigo-50 border-indigo-300 text-indigo-800'
                                : 'border-transparent text-gray-700 hover:bg-gray-50'}
                        `}
                        onClick={() => toggleExpand(node.category)}
                    >
                        {hasChildren || hasOptions || isSpotNode ? (
                            isExpanded ? <ChevronDown size={16} className={`mr-2 ${pickedCount > 0 ? 'text-indigo-600' : 'text-indigo-500'}`}/> : <ChevronRight size={16} className={`mr-2 ${pickedCount > 0 ? 'text-indigo-600' : 'text-gray-400'}`}/>
                        ) : <div className="w-6"/>}
                        <span className={pickedCount > 0 ? 'font-bold' : ''}>{node.label}</span>
                        {/* ④ 선택된 항목을 색·굵기·개수로 드러낸다 — 어디를 골랐는지 접힌 상태에서도 보인다 */}
                        {pickedCount > 0 && (
                            <span className="ml-2 text-[10px] font-bold bg-indigo-600 text-white rounded-full px-1.5 py-0.5">
                                {pickedCount}
                            </span>
                        )}
                        {node.note && (
                            <span className="ml-auto flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                <InfoTip text={node.note} />
                            </span>
                        )}
                    </div>

                    {isExpanded && (
                        <div className="animate-fadeIn">
                            {/* ① 진입점 참여 이력도 Layer 1 과 같은 선택 UI 를 쓴다
                                (타입 필터·검색·지역·회원수). 칩 목록으로는 613곳을 고를 수 없다. */}
                            {isSpotNode && (
                                <div className="mt-2 ml-4">
                                    {renderSpotList(
                                        (selectedFilters.entry_point || []).map(String),
                                        (id) => toggleFilterOption('entry_point', id),
                                    )}
                                </div>
                            )}
                            {hasChildren && (
                                <div className="mt-1">
                                    {renderTreeNodes(node.children, depth + 1)}
                                </div>
                            )}
                            {hasOptions && (
                                <div className={`flex flex-wrap gap-2 mt-2 ${depth > 0 ? 'ml-8' : 'ml-4'}`}>
                                    {node.options.map(opt => {
                                        const isSelected = (selectedFilters[node.category] || []).includes(opt.value);
                                        return (
                                            <button
                                                key={opt.value}
                                                onClick={() => toggleFilterOption(node.category, opt.value)}
                                                className={`px-3 py-1.5 text-xs rounded-full border transition-all flex items-center
                                                    ${isSelected
                                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-md'
                                                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                                                `}
                                            >
                                                {isSelected && <Check size={10} className="mr-1" />}
                                                {opt.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        });
    };

    return (
        <div className="w-full max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fadeIn">
            {/* 왼쪽: 타겟 & 필터 */}
            <div className="lg:col-span-2 space-y-8">
                <div>
                    <label className="block text-sm font-bold text-gray-700 mb-3">누구에게 보낼까요?</label>
                    <div className="grid grid-cols-2 gap-4">
                        <div onClick={() => toggleTarget('member')} className={`cursor-pointer p-4 rounded-xl border-2 flex items-center justify-between ${targets.member ? 'border-indigo-600 bg-indigo-50/50' : 'border-gray-200'}`}>
                            <div className="flex items-center">
                                <div className={`p-2 rounded-lg mr-3 ${targets.member ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}><Users size={20} /></div>
                                <div><div className="font-bold text-sm">회원 고객</div><div className="text-xs text-gray-400">서비스 가입 회원</div></div>
                            </div>
                            {targets.member && <Check size={18} className="text-indigo-600" />}
                        </div>
                        <div onClick={() => {
                            // 켜는 순간 사유를 알려준다 — 예약 확정 때야 막히면 어디를 고쳐야 할지 알기 어렵다.
                            if (isNotSendMoment) alert("쿠폰을 첨부하면 비회원 친구에게 보낼 수 없습니다.")
                            else if (!targets.nonMember && momentBlockReason) alert(`비회원 친구에게 발송할 수 없습니다.\n\n· ${momentBlockReason}`)
                            else toggleTarget('nonMember')
                        }} className={`p-4 rounded-xl border-2 flex items-center justify-between ${
                            momentAvailable === false
                                ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                                : `cursor-pointer ${targets.nonMember ? 'border-indigo-600 bg-indigo-50/50' : 'border-gray-200'}`
                        }`}>
                            <div className="flex items-center">
                                <div className={`p-2 rounded-lg mr-3 ${targets.nonMember ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-100 text-gray-400'}`}><UserPlus size={20} /></div>
                                <div>
                                    <div className="font-bold text-sm flex items-center gap-1">
                                        비회원 친구
                                        {/* 설정이 안 된 채널은 눌러도 안 되는 이유를 미리 보여준다 */}
                                        {momentAvailable === false && (
                                            <span className="text-[10px] font-normal bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                                                설정 필요
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {momentAvailable === false ? '관리자에게 문의하세요' : '채널 친구 비회원'}
                                    </div>
                                </div>
                            </div>
                            {targets.nonMember && <Check size={18} className="text-indigo-600" />}
                        </div>
                    </div>
                </div>

                {/* 트리 필터 */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 min-h-[400px]">

                    {/* [New] 필터 헤더 + 선택된 태그 리스트 */}
                    <div className="flex items-start mb-4 border-b border-gray-100 pb-4 min-h-12">
                        <label className="text-sm font-bold text-gray-700 flex items-center flex-shrink-0 mr-4 h-7">
                            <Filter size={16} className="mr-2 text-indigo-600"/> 조건 필터링
                        </label>

                        {/* 선택된 필터 태그 — 단계 순서로 정렬되고, 많아지면 줄바꿈된다.
                            4줄이 넘어가면 세로 스크롤(가로 스크롤은 뒤쪽 chip 이 안 보였다).

                            🔑 '회원 고객' 을 끄면 chip 을 **숨긴다.**
                            비회원(모먼트) 전용 발송은 조건 필터가 적용되지 않는데(targetType='moment' →
                            대상 산출을 하지 않는다) chip 이 남아 있으면 **조건이 걸린 것처럼 보인다.**
                            state 는 지우지 않는다 — 회원을 다시 켰을 때 선택이 복원되어야 한다. */}
                        <div className="flex-1 flex flex-wrap items-center gap-1.5 px-2 max-h-[7.5rem] overflow-y-auto">
                            {!targets.member && Object.keys(selectedFilters).length > 0 && (
                                <span className="text-[11px] text-gray-400">
                                    비회원 친구에게만 보낼 때는 조건 필터가 적용되지 않습니다.
                                </span>
                            )}
                            {targets.member && renderSelectedTags().map((tag, idx) => (
                                <span key={`${tag.category}-${tag.value}-${idx}`} className="flex-shrink-0 inline-flex items-center bg-indigo-50 text-indigo-700 text-[11px] px-2 py-1 rounded-md border border-indigo-100">
                                    {tag.label}
                                    <X
                                        size={10}
                                        className="ml-1 cursor-pointer hover:text-red-500"
                                        onClick={() => removeChip(tag.category, tag.value)}
                                    />
                                </span>
                            ))}
                        </div>

                        {/* 초기화 버튼 */}
                        {targets.member && Object.keys(selectedFilters).length > 0 &&
                            <button onClick={() => setSelectedFilters({})} className="flex-shrink-0 text-xs text-gray-400 hover:text-red-500 flex items-center transition-colors ml-2 h-7">
                                <CalendarDays size={12} className="mr-1"/> 초기화
                            </button>
                        }
                    </div>

                    {targets.member ? (
                        <div className="space-y-4">

                            {/* ───────── 1단계. 발송 모수 (Target) ───────── */}
                            <div className="border border-indigo-200 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between bg-indigo-50 px-4 py-2.5 border-b border-indigo-100">
                                    <span className="text-sm font-bold text-indigo-800 flex items-center">
                                        <Target size={15} className="mr-2"/> 1단계. 발송 모수 &mdash; 한 종류만 선택
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="text-xs font-bold text-indigo-700 bg-white px-2 py-0.5 rounded border border-indigo-200">
                                            {stageCounts.target.toLocaleString()}명
                                        </span>
                                        <InfoTip text="1단계 모수만 적용한 인원입니다. 아래 단계의 조건은 반영되지 않습니다.\n\n지금 발송 가능한 인원 기준이라 과거 발송 건수와는 다릅니다 — 발송 이후 채널 친구를 끊은 고객은 제외되고, 발송 건수는 사람 수가 아니라 발송 행 수입니다." />
                                    </span>
                                </div>
                                <div className="p-4 space-y-2">
                                    {[
                                        { value: 'ALL', label: '전체 회원', desc: '채널에 친구추가한 회원 전체' },
                                        { value: 'ONETIME_MSG', label: '지난 1회성 메시지 수신자', desc: '선택한 메시지를 실제로 받은 고객' },
                                        { value: 'ENTRY_POINT', label: '진입점 방문 고객', desc: '선택한 진입점으로 처음 유입된 고객 (최초 유입 기준)' },
                                        { value: 'PROJECT', label: '프로젝트 참여 고객', desc: '선택한 프로젝트에 참여한 고객' },
                                        { value: 'BENEFIT', label: '혜택 발급 고객', desc: '선택한 혜택을 발급받은 고객' },
                                    ].map(opt => (
                                        <div key={opt.value} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${poolType === opt.value ? 'border-indigo-400 bg-indigo-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                            {/* 🔴 <label> 로 감싸지 않는다 — label 이 자식의 포인터 이벤트를 라디오로
                                                재타겟해서 <input type="date"> 의 달력 아이콘이 열리지 않는다.
                                                (툴팁이 안 보였던 것과 같은 원인. 그때는 preventDefault 까지 겹쳐 있었다) */}
                                            <input type="radio" name="poolType" id={`pool-${opt.value}`} className="mt-1 accent-indigo-600 cursor-pointer"
                                                   checked={poolType === opt.value}
                                                   onChange={() => changePoolType(opt.value)} />
                                            <span className="flex-1">
                                                <label htmlFor={`pool-${opt.value}`} className="block cursor-pointer">
                                                    <span className="block text-sm font-semibold text-gray-800">{opt.label}</span>
                                                    <span className="block text-xs text-gray-500 mt-0.5">{opt.desc}</span>
                                                </label>

                                                {/* 🔑 '지난 1회성 메시지 수신자' 는 기간 입력을 두지 않는다 (2026-08-28 사용자 판단).
                                                    메시지를 직접 고르는 순간 발송 시점이 이미 확정되므로 중복이다.
                                                    백엔드의 pool_period 지원은 남겨뒀다(다른 pool 이 쓴다). */}
                                                {poolType === opt.value && opt.value !== 'ONETIME_MSG' && (
                                                    <span className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                        <span className="text-[11px] text-gray-600 font-semibold">{PERIOD_LABEL[opt.value]}</span>
                                                        <input type="date" value={periodFrom}
                                                               onChange={(e) => changePeriod(e.target.value, periodTo)}
                                                               className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-indigo-400" />
                                                        <span className="text-[11px] text-gray-400">~</span>
                                                        <input type="date" value={periodTo}
                                                               onChange={(e) => changePeriod(periodFrom, e.target.value)}
                                                               className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-indigo-400" />
                                                        {(periodFrom || periodTo) && (
                                                            <button type="button" onClick={() => changePeriod('', '')}
                                                                    className="text-[11px] text-gray-400 hover:text-red-500 px-1">지우기</button>
                                                        )}
                                                        <InfoTip text={`${PERIOD_HELP[opt.value]}\n\n비워두면 전체 기간입니다.\n한쪽만 넣으면 그 방향으로만 제한됩니다.`} />
                                                    </span>
                                                )}

                                                {opt.value === 'ONETIME_MSG' && poolType === 'ONETIME_MSG' && (
                                                    <span className="block mt-3">
                                                        <button type="button"
                                                                onClick={() => setMessagePickerOpen(o => !o)}
                                                                className="text-xs px-3 py-1.5 rounded-md border border-indigo-300 bg-white text-indigo-700 font-semibold hover:bg-indigo-50">
                                                            메시지 선택 {poolIds.length > 0 ? `(${poolIds.length}건)` : ''} {messagePickerOpen ? '▲' : '▼'}
                                                        </button>

                                                        {selectedMessages.length > 0 && (
                                                            <span className="flex flex-wrap gap-1.5 mt-2">
                                                                {selectedMessages.map(m => (
                                                                    <span key={m.id} className="inline-flex items-center bg-indigo-100 text-indigo-800 text-[11px] px-2 py-1 rounded border border-indigo-200">
                                                                        {m.campaignName || `#${m.id}`}
                                                                        <X size={10} className="ml-1 cursor-pointer hover:text-red-500" onClick={() => togglePoolId(m.id)} />
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        )}

                                                        {messagePickerOpen && (
                                                            <span className="block mt-2 border border-gray-200 rounded-lg bg-white">
                                                                <span className="block p-2 border-b border-gray-100">
                                                                    <input type="text" value={messageSearch}
                                                                           onChange={(e) => setMessageSearch(e.target.value)}
                                                                           placeholder="메시지 이름으로 검색"
                                                                           className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded outline-none focus:border-indigo-400" />
                                                                </span>
                                                                <span className="block max-h-56 overflow-y-auto">
                                                                    {sentMessages
                                                                        .filter(m => !messageSearch || (m.campaignName || '').includes(messageSearch))
                                                                        .map(m => (
                                                                        <span key={m.id}
                                                                              onClick={() => togglePoolId(m.id)}
                                                                              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b border-gray-50 last:border-0">
                                                                            <input type="checkbox" readOnly className="accent-indigo-600"
                                                                                   checked={poolIds.includes(String(m.id))} />
                                                                            <span className="flex-1 truncate text-gray-800">{m.campaignName || `#${m.id}`}</span>
                                                                            {/* 날짜 = 캠페인 식별의 1순위 정보. 발송건수는 모수 크기 감각이라 함께 남긴다.
                                                                                클릭수·클릭률은 폭을 잡아먹으므로 호버로 옮겼다. */}
                                                                            <span className="text-gray-500 flex-shrink-0 tabular-nums">
                                                                                {sendDate(m.issuedAt)}
                                                                            </span>
                                                                            <span className="text-gray-400 flex-shrink-0 tabular-nums">
                                                                                발송 {(m.successCount ?? 0).toLocaleString()}
                                                                            </span>
                                                                            <InfoTip text={[
                                                                                `발송 시각  ${sendDateTime(m.issuedAt)}`,
                                                                                `발송 시도  ${(m.sendCount ?? 0).toLocaleString()}건`,
                                                                                `발송 성공  ${(m.successCount ?? 0).toLocaleString()}건`,
                                                                                `클릭수     ${(m.linkCount ?? 0).toLocaleString()}회`,
                                                                                m.successCount > 0
                                                                                    ? `클릭률     ${(m.linkCount / m.successCount * 100).toFixed(1)}% (클릭수 ÷ 발송 성공)`
                                                                                    : '클릭률     -',
                                                                                '',
                                                                                '클릭률이 높은 메시지일수록 2단계의 \'클릭한 고객\' 조건으로 의미 있는 대상이 남습니다.',
                                                                            ].join('\n')} />
                                                                        </span>
                                                                    ))}
                                                                    {sentMessages.length === 0 && (
                                                                        <span className="block px-3 py-4 text-xs text-gray-400 text-center">발송한 1회성 메시지가 없습니다.</span>
                                                                    )}
                                                                </span>
                                                            </span>
                                                        )}
                                                    </span>
                                                )}

                                                {(opt.value === 'PROJECT' || opt.value === 'BENEFIT') && poolType === opt.value && (() => {
                                                    const isProject = opt.value === 'PROJECT';
                                                    const list = isProject ? poolProjects : poolBenefits;
                                                    const picked = list.filter(o => poolIds.includes(String(o.id)));
                                                    return (
                                                        <span className="block mt-3">
                                                            {picked.length > 0 && (
                                                                <span className="flex flex-wrap gap-1.5 mb-2">
                                                                    {picked.map(o => (
                                                                        <span key={o.id} className="inline-flex items-center bg-indigo-100 text-indigo-800 text-[11px] px-2 py-1 rounded border border-indigo-200">
                                                                            {o.name}
                                                                            <X size={10} className="ml-1 cursor-pointer hover:text-red-500" onClick={() => togglePoolId(o.id)} />
                                                                        </span>
                                                                    ))}
                                                                </span>
                                                            )}
                                                            <span className="block border border-gray-200 rounded-lg bg-white">
                                                                <span className="block p-2 border-b border-gray-100">
                                                                    <input type="text" value={genericSearch}
                                                                           onChange={(e) => setGenericSearch(e.target.value)}
                                                                           placeholder={opt.value === 'PROJECT' ? '프로젝트 이름으로 검색' : '혜택 이름으로 검색'}
                                                                           className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded outline-none focus:border-indigo-400" />
                                                                </span>
                                                                <span className="block max-h-56 overflow-y-auto">
                                                                    {list.filter(o => !genericSearch || String(o.name).includes(genericSearch)).map(o => (
                                                                        <span key={o.id}
                                                                              onClick={() => togglePoolId(o.id)}
                                                                              className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-indigo-50 cursor-pointer border-b border-gray-50 last:border-0">
                                                                            <input type="checkbox" readOnly className="accent-indigo-600"
                                                                                   checked={poolIds.includes(String(o.id))} />
                                                                            {isProject ? (
                                                                                <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded ${o.running ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                                    {o.running ? '운영중' : '종료'}
                                                                                </span>
                                                                            ) : (
                                                                                <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded ${o.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                                                                                    {o.status === 'ACTIVE' ? '사용중' : '중지'}
                                                                                </span>
                                                                            )}
                                                                            <span className="flex-1 truncate text-gray-800">{o.name}</span>
                                                                            {isProject ? (
                                                                                <span className="text-gray-400 flex-shrink-0 tabular-nums">
                                                                                    {sendDate(o.startDate)}{o.endDate ? ` ~ ${sendDate(o.endDate)}` : ' ~'}
                                                                                </span>
                                                                            ) : (
                                                                                <>
                                                                                    <span className="text-gray-500 flex-shrink-0 tabular-nums">{sendDate(o.createdAt)}</span>
                                                                                    <span className="text-gray-500 flex-shrink-0 tabular-nums">발급 {(o.issuedCount ?? 0).toLocaleString()}</span>
                                                                                </>
                                                                            )}
                                                                            <InfoTip text={isProject
                                                                                ? [
                                                                                    `상태       ${o.running ? '운영중' : '종료'}`,
                                                                                    `운영 기간  ${sendDate(o.startDate)} ~ ${o.endDate ? sendDate(o.endDate) : '종료일 없음'}`,
                                                                                    `생성일     ${sendDate(o.createdAt)}`,
                                                                                  ].join('\n')
                                                                                : [
                                                                                    `상태       ${o.status === 'ACTIVE' ? '사용중' : '중지'}`,
                                                                                    `혜택 종류  ${o.type}`,
                                                                                    `생성일     ${sendDate(o.createdAt)}`,
                                                                                    `발급 건수  ${(o.issuedCount ?? 0).toLocaleString()}건`,
                                                                                    o.retailPrice ? `정상가     ${o.retailPrice.toLocaleString()}원` : null,
                                                                                    '',
                                                                                    '발급 건수가 0이면 이 혜택으로는 모수가 비어 있습니다.',
                                                                                  ].filter(Boolean).join('\n')} />
                                                                            {!isProject && o.imageUrl && (
                                                                                <img src={o.imageUrl} alt="" className="w-8 h-8 object-cover rounded flex-shrink-0 border border-gray-200" />
                                                                            )}
                                                                        </span>
                                                                    ))}
                                                                    {list.length === 0 && (
                                                                        <span className="block px-3 py-4 text-xs text-gray-400 text-center">목록이 없습니다.</span>
                                                                    )}
                                                                </span>
                                                            </span>
                                                        </span>
                                                    );
                                                })()}

                                                {opt.value === 'ENTRY_POINT' && poolType === 'ENTRY_POINT' && (
                                                    <span className="block mt-3">
                                                        <button type="button"
                                                                onClick={() => setSpotPickerOpen(o => !o)}
                                                                className="text-xs px-3 py-1.5 rounded-md border border-indigo-300 bg-white text-indigo-700 font-semibold hover:bg-indigo-50">
                                                            진입점 선택 {poolIds.length > 0 ? `(${poolIds.length}곳)` : ''} {spotPickerOpen ? '▲' : '▼'}
                                                        </button>

                                                        {selectedSpots.length > 0 && (
                                                            <span className="flex flex-wrap gap-1.5 mt-2">
                                                                {selectedSpots.map(sp => (
                                                                    <span key={sp.id} className="inline-flex items-center bg-indigo-100 text-indigo-800 text-[11px] px-2 py-1 rounded border border-indigo-200">
                                                                        {sp.name}
                                                                        <X size={10} className="ml-1 cursor-pointer hover:text-red-500" onClick={() => togglePoolId(sp.id)} />
                                                                    </span>
                                                                ))}
                                                            </span>
                                                        )}

                                                        {spotPickerOpen && renderSpotList(poolIds, togglePoolId)}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    ))}
                                    <p className="text-[11px] text-gray-500 pt-1">
                                        ⓘ 같은 종류 안에서는 여러 개를 고를 수 있습니다(합집합). 기간을 비우면 전체 기간입니다.
                                    </p>
                                    <p className="text-[11px] text-gray-500 flex items-center gap-1">
                                        <span>ⓘ 비회원 대상 발송 메시지 안내</span>
                                        <InfoTip text={"비회원 발송은 카카오 채널 구독자 전체를 대상으로 하되 회원 발송 대상자만 제외하고 보내기 때문에, 누가 받았는지 알 수 없습니다.\n\n그래서 비회원 발송은 발송 모수로 선택할 수 없습니다.\n\n혼합 발송(회원 + 비회원)한 메시지를 고른 경우에도 비회원 발송 내역은 제외한 회원 대상 수신자만 모수가 됩니다."} />
                                    </p>
                                </div>
                            </div>

                            <div className="text-center text-xs text-gray-400 font-semibold">
                                ↓ 이 모수 중에서 (AND)
                                {stageCounts.target > stageCounts.engagement && (
                                    <span className="ml-2 text-emerald-600">
                                        −{(stageCounts.target - stageCounts.engagement).toLocaleString()}명
                                    </span>
                                )}
                            </div>

                            {/* ───────── 2단계. 반응 조건 (Engagement) ───────── */}
                            <div className="border border-emerald-200 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between bg-emerald-50 px-4 py-2.5 border-b border-emerald-100">
                                    <span className="text-sm font-bold text-emerald-800 flex items-center">
                                        <MousePointerClick size={15} className="mr-2"/> 2단계. 반응 조건
                                    </span>
                                    <span className="text-xs font-bold text-emerald-700 bg-white px-2 py-0.5 rounded border border-emerald-200">
                                        {stageCounts.engagement.toLocaleString()}명
                                    </span>
                                </div>
                                <div className="p-4 space-y-2">
                                    {isMessagePool ? (
                                        <>
                                            {[
                                                { value: 'ALL', label: '전체',
                                                  desc: '발송 성공 기준 · 현재 발송 가능 예상 수' },
                                                { value: 'CLICKED', label: `위에서 선택한 메시지${poolIds.length ? `(${poolIds.length}건)` : ''}를 클릭한 고객`,
                                                  desc: '발송 성공 기준 · 그중 링크를 누른 고객' },
                                                { value: 'NOT_CLICKED', label: '위 메시지를 클릭하지 않은 고객',
                                                  desc: '발송 성공 기준 · 그중 링크를 누르지 않은 고객',
                                                  warn: '클릭 추적은 버튼·이미지·쿠폰 링크만 집계합니다. 본문에 직접 쓴 주소나 채널추가 버튼은 기록되지 않으므로, 실제로는 클릭했지만 이 조건에 포함되는 고객이 있을 수 있습니다.' },
                                            ].map(opt => (
                                                <div key={opt.value} className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${reactType === opt.value ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'} ${poolIds.length === 0 && opt.value !== 'ALL' ? 'opacity-40 pointer-events-none' : ''}`}>
                                                    {/* 🔴 <label> 은 **텍스트만** 감싼다. 컨트롤까지 감싸면 label 이 자식 포인터
                                                        이벤트를 라디오로 재타겟해 date picker 달력이 열리지 않고 툴팁 hover 도 죽는다.
                                                        1단계(pool) 행과 같은 구조다 — 기간 입력칸이 **이 박스 안**에 들어간다. */}
                                                    <input type="radio" name="reactType" id={`react-${opt.value}`} className="mt-0.5 accent-emerald-600 cursor-pointer"
                                                           checked={reactType === opt.value}
                                                           onChange={() => changeReactType(opt.value)} />
                                                    <span className="flex-1">
                                                        <label htmlFor={`react-${opt.value}`} className="block cursor-pointer">
                                                            <span className="block text-sm text-gray-800">{opt.label}</span>
                                                            {opt.desc && <span className="block text-[11px] text-gray-500 mt-0.5">{opt.desc}</span>}
                                                        </label>

                                                        {opt.value === 'CLICKED' && reactType === 'CLICKED' && (
                                                            <span className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                                <select value={reactCount}
                                                                        onChange={(e) => changeReactCount(e.target.value)}
                                                                        className="text-xs border border-gray-300 rounded px-2 py-1 outline-none flex-shrink-0">
                                                                    <option value="1">1회 이상</option>
                                                                    <option value="2">2회 이상</option>
                                                                    <option value="3">3회 이상</option>
                                                                </select>
                                                                {/* 클릭 시점 기간(XS-08). 1단계 '발송 기간' 과 **다른 축**이다.
                                                                    라벨은 '최근' 으로 쓴다 — '지난 N개월' 은 발송일 기준으로 읽힐 수 있다. */}
                                                                <select value={reactPeriodSelect}
                                                                        onChange={(e) => {
                                                                            if (e.target.value === 'CUSTOM') {
                                                                                // 빈 구간이 저장되지 않게 오늘까지 1개월을 채운다.
                                                                                const to = new Date();
                                                                                const from = new Date(); from.setMonth(from.getMonth() - 1);
                                                                                const fmt = (d) => d.toISOString().slice(0, 10);
                                                                                setReactPeriod(`${fmt(from)}~${fmt(to)}`);
                                                                            } else {
                                                                                setReactPeriod(e.target.value);
                                                                            }
                                                                        }}
                                                                        className="text-xs border border-gray-300 rounded px-2 py-1 outline-none flex-shrink-0">
                                                                    <option value="">클릭 시점 전체</option>
                                                                    {Array.from({length: 12}, (_, i) => (
                                                                        <option key={i+1} value={String(i+1)}>오늘부터 최근 {i+1}개월 이내 클릭</option>
                                                                    ))}
                                                                    <option value="CUSTOM">클릭 시점 직접 입력</option>
                                                                </select>
                                                                <InfoTip text={"고객이 링크를 **누른** 시점 기준입니다. 메시지가 발송된 시점이 아닙니다.\n\n'최근 N개월' = 오늘로부터 거꾸로 N개월(발송일 기준이 아닙니다).\n예: 3개월 전에 보낸 메시지를 어제 눌렀다면 '최근 1개월 이내 클릭'에 포함됩니다.\n\n'클릭 시점 전체' 는 기간 제한 없음입니다."} />

                                                                {/* 🔑 직접 입력 칸을 **같은 박스 안**에 둔다(사용자 지적).
                                                                    종전에는 2단계 목록 맨 아래에 떨어져 나와 '반응이 없다' 고 오해됐다. */}
                                                                {isReactPeriodCustom && (
                                                                    <span className="flex items-center gap-1.5 flex-wrap basis-full mt-1">
                                                                        <span className="text-[11px] text-gray-600 font-semibold">클릭 기간</span>
                                                                        <input type="date" value={rpRange[0] || ''}
                                                                               onChange={(e) => setReactPeriod(`${e.target.value}~${rpRange[1] || ''}`)}
                                                                               className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-emerald-400" />
                                                                        <span className="text-[11px] text-gray-400">~</span>
                                                                        <input type="date" value={rpRange[1] || ''}
                                                                               onChange={(e) => setReactPeriod(`${rpRange[0] || ''}~${e.target.value}`)}
                                                                               className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-emerald-400" />
                                                                        <button type="button" onClick={() => setReactPeriod('')}
                                                                                className="text-[11px] text-gray-400 hover:text-red-500 px-1">지우기</button>
                                                                        <InfoTip text={"한쪽만 넣으면 그 방향으로만 제한됩니다.\n\n'지우기' 를 누르면 클릭 시점 제한이 없어집니다."} />
                                                                    </span>
                                                                )}

                                                                {Number(reactCount) > 1 && poolIds.length > 1 && (
                                                                    <span className="block basis-full text-[11px] text-amber-600 mt-1">
                                                                        ⓘ 메시지 {poolIds.length}건을 골랐습니다 — '{reactCount}회 이상'은 <b>메시지별이 아니라 {poolIds.length}건을 합산한 탭 수</b>입니다.
                                                                    </span>
                                                                )}
                                                                {reactPeriodValue !== '' && Number(reactCount) > 1 && (
                                                                    <span className="block basis-full text-[11px] text-amber-600">
                                                                        ⓘ '{reactCount}회 이상'은 <b>이 클릭 기간 안에서</b> 누른 횟수로 셉니다.
                                                                    </span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {opt.warn && <InfoTip text={opt.warn} />}
                                                </div>
                                            ))}
                                            {reactType === 'NOT_CLICKED' && (
                                                <p className="text-[11px] text-gray-500 pl-8">
                                                    ⓘ '클릭하지 않은 고객'은 <b>기간과 무관하게 한 번도 누르지 않은 고객</b>입니다.
                                                </p>
                                            )}
                                            {poolIds.length === 0 && (
                                                <p className="text-[11px] text-amber-600 pt-1">ⓘ 1단계에서 메시지를 먼저 선택해야 반응 조건을 쓸 수 있습니다.</p>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {/* 🔴 '전체' = 반응 조건이 **하나도 없는** 상태다.
                                                종전에는 `!channelClickCategory` 만 봐서, 혜택 반응을 고르면
                                                (respond_N 이 없으므로) 이 값이 true 가 되어 **색만 강조**됐다.
                                                라디오는 같은 name 이라 DOM 이 하나만 체크하므로
                                                '체크는 혜택 쪽, 색은 전체' 라는 어긋난 상태가 보였다. */}
                                            <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${!channelClickCategory && reactType === 'ALL' ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                                <input type="radio" name="reactType" className="accent-emerald-600"
                                                       checked={!channelClickCategory && reactType === 'ALL'}
                                                       onChange={() => setChannelClick(null)} />
                                                <span className="flex-1">
                                                    <span className="block text-sm text-gray-800">전체</span>
                                                    <span className="block text-[11px] text-gray-500 mt-0.5">현재 발송 가능 예상 수</span>
                                                </span>
                                            </label>
                                            {/* 🔑 종전에는 라디오 3개(`respond_1/2/3`)였다 — 메시지 모수는
                                                '라디오 1개 + 횟수 풀다운' 인데 여기만 달라 UI 가 어긋났다(E: 통일).
                                                🟢 **백엔드 무변경** — 횟수가 와이어 키에 들어 있으므로
                                                풀다운이 키를 갈아끼운다(`setChannelClick` 이 이미 구 키를 지운다).
                                                저장된 예약도 그대로 열린다(키·값 형식 무변경). */}
                                            {(() => {
                                                const cat = channelClickCategory || 'respond_1';
                                                const cnt = cat.replace('respond_', '');
                                                const selected = !!channelClickCategory;
                                                return (
                                            <div className={`flex items-start gap-3 p-3 rounded-lg border ${selected ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                                <input type="radio" name="reactType" id="ch-click" className="mt-0.5 accent-emerald-600 cursor-pointer"
                                                       checked={selected}
                                                       onChange={() => setChannelClick('respond_1', channelClickMonth)} />
                                                <span className="flex-1">
                                                    <label htmlFor="ch-click" className="block cursor-pointer">
                                                        <span className="block text-sm text-gray-800">채널의 아무 메시지나 클릭한 고객</span>
                                                        <span className="block text-[11px] text-gray-500 mt-0.5">채널에서 받은 아무 메시지나 기준 · 특정 메시지와 무관</span>
                                                    </label>
                                                    {selected && (
                                                        <span className="flex items-center gap-1.5 mt-2 flex-wrap">
                                                            <select value={cnt}
                                                                    onChange={(e) => setChannelClick(`respond_${e.target.value}`, channelClickMonth)}
                                                                    className="text-xs border border-gray-300 rounded px-2 py-1 outline-none flex-shrink-0">
                                                                <option value="1">1회 이상</option>
                                                                <option value="2">2회 이상</option>
                                                                <option value="3">3회 이상</option>
                                                            </select>
                                                            <select value={channelClickMonth}
                                                                    onChange={(e) => {
                                                                        if (e.target.value === 'CUSTOM') {
                                                                            const to = new Date();
                                                                            const from = new Date(); from.setMonth(from.getMonth() - 1);
                                                                            const fmt = (d) => d.toISOString().slice(0, 10);
                                                                            setChannelClick(cat, `${fmt(from)}~${fmt(to)}`);
                                                                        } else {
                                                                            setChannelClick(cat, e.target.value);
                                                                        }
                                                                    }}
                                                                    className="text-xs border border-gray-300 rounded px-2 py-1 outline-none flex-shrink-0">
                                                                {Array.from({length: 12}, (_, i) => (
                                                                    <option key={i+1} value={String(i+1)}>오늘부터 최근 {i+1}개월 이내 클릭</option>
                                                                ))}
                                                                <option value="CUSTOM">클릭 시점 직접 입력</option>
                                                            </select>
                                                            <InfoTip text={"클릭이 발생한 시점 기준입니다. 메시지가 발송된 시점이 아닙니다.\n\n'최근 N개월' = 오늘로부터 거꾸로 N개월(발송일 기준이 아닙니다).\n예: 3개월 전에 보낸 메시지를 어제 눌렀다면 '최근 1개월 이내 클릭'에 포함됩니다.\n\n'N회 이상'은 채널에서 받은 메시지들의 탭을 사람별로 합산합니다."} />

                                                            {isChannelClickCustom && (
                                                                <span className="flex items-center gap-1.5 flex-wrap basis-full mt-1">
                                                                    <span className="text-[11px] text-gray-600 font-semibold">클릭 기간</span>
                                                                    <input type="date" value={ccRange[0] || ''}
                                                                           onChange={(e) => setChannelClick(cat, `${e.target.value}~${ccRange[1] || ''}`)}
                                                                           className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-emerald-400" />
                                                                    <span className="text-[11px] text-gray-400">~</span>
                                                                    <input type="date" value={ccRange[1] || ''}
                                                                           onChange={(e) => setChannelClick(cat, `${ccRange[0] || ''}~${e.target.value}`)}
                                                                           className="text-[11px] border border-gray-200 rounded px-1.5 py-1 outline-none focus:border-emerald-400" />
                                                                    <button type="button" onClick={() => setChannelClick(cat, '1')}
                                                                            className="text-[11px] text-gray-400 hover:text-red-500 px-1">개월 선택으로</button>
                                                                </span>
                                                            )}
                                                        </span>
                                                    )}
                                                </span>
                                            </div>
                                                );
                                            })()}

                                        </>
                                    )}

                                    {/* 🔑 혜택 반응은 **if/else 밖**에 둔다 (2026-08-31, XS-08 E+F).
                                        모수별 노출 여부는 `BENEFIT_REACTIONS` 표가 결정하고, 렌더는 한 곳이다.
                                        분기 안에 두면 모수가 늘 때마다 같은 UI 를 또 복사하게 된다. */}
                                    {benefitReactions.map(v => renderBenefitRow(v))}

                                    {/* 🔴 라디오 목록 **맨 아래**에 둔다. 종전에는 메시지 분기 끝에 있어서
                                        혜택 반응 행(분기 밖)보다 위 = 목록 중간에 끼어 있었다(사용자 지적). */}
                                    {isMessagePool && (
                                        <p className="text-[11px] text-gray-500 pt-1 flex items-start gap-1">
                                            <span>ⓘ '2회 이상'은 <b>한 사람이 링크를 2번 이상 누른 경우</b>를 말합니다.</span>
                                            <InfoTip text={"세는 규칙: 고른 메시지 전체에서 나온 탭을 **사람별로 합산**합니다.\n\n· 메시지 1건만 고른 경우 → 그 메시지를 2번 누른 사람\n· 메시지 여러 건을 고른 경우 → 합계가 2회면 통과합니다.\n  (예: A 메시지 1번 + B 메시지 1번 = 2회 → '2회 이상'에 포함)\n\n메시지별로 각각 2회 이상 누른 사람을 뽑는 기능은 없습니다.\n\n같은 버튼을 두 번 눌러도 2회로 셉니다(탭 수 기준).\n\n참고로 대량 발송 메시지는 클릭한 고객 1명당 평균 1~2회 정도만 누릅니다. '3회 이상'을 고르면 대상이 거의 남지 않을 수 있습니다."} />
                                        </p>
                                    )}

                                    {poolIds.length === 0 && benefitReactions.length > 0 && !isMessagePool && (
                                        <p className="text-[11px] text-amber-600 pt-1">ⓘ 1단계에서 대상을 먼저 선택해야 혜택 조건을 쓸 수 있습니다.</p>
                                    )}
                                </div>
                            </div>

                            <div className="text-center text-xs text-gray-400 font-semibold">
                                ↓ 이 조건 중에서 (AND)
                                {stageCounts.engagement > memberCount && (
                                    <span className="ml-2 text-gray-600">
                                        −{(stageCounts.engagement - memberCount).toLocaleString()}명
                                    </span>
                                )}
                            </div>

                            {/* ───────── 3단계. 고객 속성 (Data filter) ───────── */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="flex items-center justify-between bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                                    <span className="text-sm font-bold text-gray-700 flex items-center">
                                        <Filter size={15} className="mr-2"/> 3단계. 고객 속성 &mdash; 선택
                                        <span className="text-[11px] font-normal text-gray-500 ml-3 flex items-center gap-1">
                                            항목끼리는 <b className="text-gray-700">AND</b>, 항목 안의 값끼리는 <b className="text-gray-700">OR</b>
                                            <InfoTip text={"· 서로 다른 항목을 고르면 **모두 만족**하는 고객만 남습니다 (AND)\n· 같은 항목 안에서 여러 값을 고르면 **하나라도 맞는** 고객이 남습니다 (OR)\n\n예) 성별 '남성' + 연령대 '20-29세', '30-39세'\n  → 남성이면서, 20대이거나 30대인 고객\n  → 남성 20대 + 남성 30대"} />
                                        </span>
                                    </span>
                                    <span className="text-xs font-bold text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200">
                                        {memberCount.toLocaleString()}명
                                    </span>
                                </div>
                                <div className="p-4 space-y-2">
                                    {renderTreeNodes(filterTree)}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="h-64 flex flex-col items-center justify-center text-gray-400 opacity-50">
                            <Filter size={48} className="mb-3 stroke-1" />
                            <p className="text-sm">회원 고객을 선택해야 필터를 사용할 수 있습니다.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* 오른쪽: 설정 & 요약 (Sticky) */}
            <div className="flex flex-col gap-4 sticky top-6 h-fit">
                <button
                    onClick={onTestSend}
                    className="w-full py-3 bg-white border border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm flex items-center justify-center group"
                >
                    <Send size={18} className="mr-2 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                    나에게 테스트 발송
                </button>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">상세 설정</h3>
                    <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
                        <span className="text-sm font-medium text-gray-700">연령 인증 (성인)</span>
                        <button onClick={() => setIsAdult(!isAdult)} className={`relative w-10 h-5 rounded-full transition-colors ${isAdult ? 'bg-red-500' : 'bg-gray-300'}`}><div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isAdult ? 'translate-x-5' : ''}`}></div></button>
                    </div>
                    <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm space-y-2">
                        <div className="flex items-center text-sm font-medium text-gray-700 mb-1"><Calendar size={14} className="mr-2 text-indigo-500"/> 발송 예약</div>
                        <input type="date" className="w-full text-sm border-gray-200 rounded bg-gray-50 p-1.5 focus:ring-1 focus:ring-indigo-500 outline-none"
                               value={reserveDate?.date}
                               onChange={(e) => setReserveDate({...reserveDate, date: e.target.value})}
                        />
                        <div className="flex gap-1">
                            <select className="flex-1 text-sm border-gray-200 rounded bg-gray-50 p-1.5 outline-none"
                                    value={reserveDate?.hour}
                                    onChange={e=>
                                        setReserveDate({...reserveDate, hour: e.target.value})}
                            >
                                {
                                    Array.from({length:13}, (_, i) => i + 8)
                                        .map(h => <option key={h} value={String(h).padStart(2,'0')}>{String(h).padStart(2,'0')}시</option>)
                                }
                            </select>
                            <select className="flex-1 text-sm border-gray-200 rounded bg-gray-50 p-1.5 outline-none"
                                    value={reserveDate?.min}
                                    onChange={e=>
                                        setReserveDate({...reserveDate, min: e.target.value})}
                            >
                                {['00','10','20','30','40','50'].map(m => <option key={m} value={m}>{m}분</option>)}
                            </select>
                        </div>
                        <div className="text-[10px] text-gray-400 mt-1 flex items-start">
                            <AlertCircle size={10} className="mr-1 mt-0.5 flex-shrink-0"/>
                            <span>20:55 ~ 08:00 발송 불가</span>
                        </div>
                    </div>
                </div>

                <div className="bg-indigo-900 text-white p-5 rounded-xl shadow-lg">
                    <div className="flex items-center justify-between mb-4 pb-4 border-b border-indigo-700/50">
                        <div className="text-indigo-200 text-sm">예상 발송 대상</div>
                        <div className="font-bold text-xl">{totalCount.toLocaleString()} <span className="text-sm font-normal">건</span></div>
                    </div>
                    <div className="space-y-2 text-sm text-indigo-200 mb-4">
                        <div className="flex justify-between"><span>회원 ({targets.member ? 'ON' : 'OFF'})</span><span>{memberCount.toLocaleString()}</span></div>
                        <div className="flex justify-between"><span>비회원 ({targets.nonMember ? 'ON' : 'OFF'})</span><span>{nonMemberCount.toLocaleString()}</span></div>
                    </div>
                    <div className="bg-indigo-800 rounded-lg p-3 flex justify-between items-center">
                        <div className="flex items-center text-sm font-medium"><Coins size={16} className="mr-2 text-yellow-400"/> 예상 비용</div>
                        <div className="font-bold text-lg text-yellow-400">{totalCost.toLocaleString()} <span className="text-xs text-indigo-300">원</span></div>
                    </div>
                    <p className="text-[11px] text-indigo-300 leading-relaxed mt-3">
                        현재 시점의 예상 발송량이며, 실제 발송 시점과 회원의 상태에 따라 달라질 수 있습니다.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default OneTimeMessageSegment;